import './public-path';
/**
 * Global imports
 */
import Vue from 'vue';
import VueTranslate from './vue-translate-plugin'; // modified version  to be case insensitive for string resource keys
import App from './App.vue';
import router from './router';
import store from './store';
import SignalrRequestService from './services/signalr-request-service';
import { EventBus } from './services/event-bus-service';
import { BTO_EMITERS, BTO_LISTENERS } from './models/enums/bto-event-bus-const';
import { LAYOUT_NAME } from './models/enums/layout-const';

/**
 * Services import
 */
import ErrorService from './services/error-service';
import LanguageRequestService from './services/language/language-request-service';
import AllLayoutsRequestService from './services/all-layouts-request-service';
import LocalStorageService from './services/storage/local-storage-service';
import { ImageStorageService } from './services/storage/image-storage.service';
import { initBtoPublicBus, BtoPublicBus } from './services/bto-event-bus';
import AspireService from './services/aspire.service';
import SignalrCoreService from './services/signalr-core.service';
import { initToastState } from '@bto-services/toast-notification';
import { getLimitsByCurrencies, getSettings, useCashout } from '@bto-services/api/shared/sb-settings';
import ConfigService from '@bto-services/api/shared/config';
import OldConfigService from './services/config';
import { userLoggedIn, watchBtoUser } from '@bto-services/user';
import { setBetslipSettings, fetchSpecialBetCombinations, setLimitByCurrency } from '@bto-services/betslip-core';

import { BETSLIP_SETTINGS } from 'bto-models/betslip';

/**
 * Global filters
 */
import './filters/global';
/**
 * All global components
 */
import BetslipWidget from '@bto-widgets/betslip/src/web';
Vue.component('betslip-widget', BetslipWidget);
// eslint-disable-next-line no-unused-vars
import * as global from './components/global-components';

Vue.use(VueTranslate);
/**
 *Add dataLayer to window object
 */
window.dataLayer = window.dataLayer || [];
/**
 * global var to display all hidden errors
 * to view errors just call in console sbGlobalErrors
 */
window.sbGlobalErrors = ErrorService.getHiddenErrors();
window.btoLangId = 1;
Vue.config.productionTip = false;

function updateSettings() {
  // Get SB Settings, and initialize defaults for formatting
  getSettings().then((settings) => {
    if (!APP_SETTINGS.player.currency && APP_SETTINGS.currencyFormat.showDefaultCurrency) {
      APP_SETTINGS.player.currency = settings.Settings[BETSLIP_SETTINGS.DEFAULT_CURRENCY] || '';
    }
    const currency = APP_SETTINGS.player.currency;
    APP_SETTINGS.currencyFormat.rd = settings.RoundDec[currency] && settings.RoundDec[currency].rd || 2;
    setBetslipSettings({
      ...settings,
      playerCurrency: APP_SETTINGS.player.currency,
    });
    APP_SETTINGS.settings.canViewLiveStreamNoBalance = settings.Settings[BETSLIP_SETTINGS.CAN_VIEW_LIVE_STREAM_WITHOUT_BALANCE];
    getLimitsByCurrencies().then(setLimitByCurrency);
  });
}

export default new Vue({
  data: {
    signalR: typeof jQuery !== 'undefined' && typeof jQuery.signalR !== 'undefined' ? jQuery.signalR : null,
    signalRConnection: typeof jQuery !== 'undefined' && typeof jQuery.connection !== 'undefined' ? jQuery.connection : null,
    signalrRequest: null,
    oddFormat: localStorage.getItem('oddFormat'),
    timeFormat: localStorage.getItem('timeFormat'),
    statscore: {},
    statscoreLang: '',
  },
  beforeCreate() {
    this.$translate.$once('locales:loaded', () => {
      // connect to SignalR and make the object accessible in app
      this.signalrRequest = APP_SETTINGS.path.signalrUrl ? new SignalrCoreService(this) : new SignalrRequestService(this);
      document.getElementById('bto-sb-mb-splash-content').remove();
      document.getElementById('sportbook').style.visibility = 'visible';
      BtoPublicBus.emit(BTO_EMITERS.APP_LOADED);
    });
    // get language strings from server API
    let languageRequest = new LanguageRequestService();
    languageRequest.getNewLanguage(this.$translate);
    LocalStorageService.remove('lbst');

    // Load main layout data
    let allLayoutsRequest = new AllLayoutsRequestService();
    allLayoutsRequest.getLayouts();
    // Load betslip settings
    updateSettings();
    /**
     * call the API to get the player related settings from SB and store them.
     */
    useCashout().then(val => {
      this.$store.dispatch("setCashoutDbSetting", val);
      APP_SETTINGS.settings.useCashout = val;
      if (val && userLoggedIn()) {
        store.dispatch('getPlayerInfo');
      }
    });
  },
  created() {
    if (this.$route.meta && this.$route.meta.hideAfterRouterWidgets) {
      store.commit('hideAfterRouteWidgets');
    }
    // Get Main Widget Markets
    store.dispatch('getSportComboData');
    // Get Main Widget Markets for Us
    if (APP_SETTINGS.settings.layout === LAYOUT_NAME.US) {
      store.dispatch('getSportComboDataUs');
    }

    // preload all sports from server
    store.dispatch('getAllSportData');
    // Get live match details sport tabs config
    store.dispatch('getLiveMatchTabs');

    if (!localStorage.getItem('oddFormat')) {
      localStorage.setItem('oddFormat', APP_SETTINGS.formatting.defaultOutcomeType);
    }
    if (!localStorage.getItem('timeFormat')) {
      localStorage.setItem('timeFormat', APP_SETTINGS.formatting.defaultOutcomeType);
    }
    EventBus.$on('oddFormat', this.changeOddFormat);
    store.commit('initFavorite');
    fetchSpecialBetCombinations();

    this.changeStatscoreLang();
    AspireService.onSessionUpdate(this);
    watchBtoUser(this.handleBtoUserChange);
    initToastState();
  },
  mounted() {
    const imageStore = new ImageStorageService();
    imageStore.checkImageCache();
    initBtoPublicBus();
    BtoPublicBus.on(BTO_LISTENERS.LOGIN, this.login);
    BtoPublicBus.on(BTO_LISTENERS.LOGOUT, this.logout);
    BtoPublicBus.on(BTO_LISTENERS.CHANGE_LANG, this.handleLanguageChange);
    AspireService.setKeepAlive();
  },
  beforeDestroy() {
    BtoPublicBus.off(BTO_LISTENERS.LOGIN, this.login);
    BtoPublicBus.off(BTO_LISTENERS.LOGOUT, this.logout);
    BtoPublicBus.off(BTO_LISTENERS.CHANGE_LANG, this.handleLanguageChange);
    EventBus.$off('oddFormat', this.changeOddFormat);
    AspireService.removeKeepAlive();
  },
  methods: {
    handleBtoUserChange() {
      // eslint-disable-next-line
      const externalBtoUser = btoUser;
      const newPlayer = Object.keys(externalBtoUser).reduce((acc, k) => {
        if (externalBtoUser[k]) {
          acc[k] = externalBtoUser[k];
        }
        return acc;
      }, {});

      if (!newPlayer.token) {
        this.logout();
        return;
      }
      this.login(newPlayer);
    },
    /**
     * Login player and reset player state
     * @param {{id:string,token:string,currency:string,language?:string}} newPlayer - new player object
     */
    login(newPlayer = {}) {
      const oldLanguage = APP_SETTINGS.player.language;

      ConfigService.defaultHeaders.Culture = newPlayer.language || oldLanguage;
      OldConfigService.defaultHeaders.Culture = newPlayer.language || oldLanguage;
      ConfigService.authorizationHeaders.Token = newPlayer.token;
      OldConfigService.authorizationHeaders.Token = newPlayer.token;
      store.dispatch('login', newPlayer);
      updateSettings();

      this.signalrRequest.reconnect();
      this.changeLanguage(oldLanguage, newPlayer);
    },
    /**
     * Logout player and reset player state
     */
    logout() {
      ConfigService.authorizationHeaders.Token = '';
      OldConfigService.authorizationHeaders.Token = '';
      store.dispatch('logout');
      updateSettings();
      this.signalrRequest.reconnect();
    },
    /**
     * Handler for external change language event
     * @param {string} newLanguage - new language
     */
    handleLanguageChange(newLanguage = '') {
      if (!newLanguage) {
        return;
      }
      const oldLanguage = APP_SETTINGS.player.language;
      ConfigService.defaultHeaders.Culture = newLanguage;
      OldConfigService.defaultHeaders.Culture = newLanguage;
      const newPlayer = { ...APP_SETTINGS.player, language: newLanguage };
      store.dispatch('login', newPlayer);
      this.signalrRequest.reconnect();
      this.changeLanguage(oldLanguage, newPlayer);
    },
    /**
     * Check for language change and get new translation if changed
     * @param {string} oldLanguage - current language
     * @param {{language?:string}} newPlayer - new player object
     */
    changeLanguage(oldLanguage, newPlayer) {
      if (newPlayer && newPlayer.language && newPlayer.language !== oldLanguage) {
        store.dispatch('getAllSportData');
        store.dispatch('getLiveMatchTabs');
        this.changeStatscoreLang();
        this.changeTranslations();
      }
    },
    /**
     * Get new translations and change them
     */
    changeTranslations() {
      let languageRequest = new LanguageRequestService();
      languageRequest.getNewLanguage(this.$translate);
    },
    /**
     * Check for statscore supported languages and change it accordingly
     */
    changeStatscoreLang() {
      if (APP_SETTINGS.liveTracker.provider === 'statscore') {
        let statscoreSupportedLanguages = [
          'bg', 'br', 'cs', 'de', 'el',
          'en', 'es', 'fi', 'fr', 'hr', 'id', 'it', 'ka', 'ko', 'mx', 'nl',
          'no', 'pl', 'pt', 'ro', 'ru', 'sr', 'sv', 'th', 'tr', 'vi', 'zh'
        ];

        if (
          APP_SETTINGS.player.language
          &&
          statscoreSupportedLanguages.includes(APP_SETTINGS.player.language.toLowerCase())
        ) {
          this.statscoreLang = APP_SETTINGS.player.language;
        } else {
          this.statscoreLang = 'en';
        }
      }
    },
    /**
     * Change odd format
     * @param {'decimal'|'fractional'|'american'} oddFormat - new odd format
     */
    changeOddFormat(oddFormat) {
      this.oddFormat = oddFormat;
    },
  },
  router,
  store,
  components: { App },
  render: h => h(App)
}).$mount('#sportbook');
